import { LogAmplitudeEvent, useAnalytics } from "@codat/analytics";
import {
  Integration,
  useToggleIntegrationMutation as toggleIntegrationMutation,
} from "api/mutations/useToggleIntegrationMutation";
import {
  LogoResponse,
  useGetLogoQuery as getLogoQuery,
} from "api/queries/useGetLogoQuery";
import { RefetchFunction } from "axios-hooks";
import { useEffect, useState } from "react";

enum NoSetupIntegrations {
  BigCommerce = "BigCommerce",
  ClearBooks = "Clear Books",
  Kashflow = "KashFlow",
  OracleNetSuite = "Oracle NetSuite",
  PrestaShop = "PrestaShop",
}

const commerceIntegrationsKeys = {
  bigCommerce: "vqzp",
  prestaShop: "zgbz",
};

const accountingIntegrationsKeys = {
  clearBooks: "jhch",
  kashFlow: "wvzu",
  oracleNetSuite: "akxx",
};

interface Logos {
  bigCommerceLogo: LogoResponse;
  clearBooksLogo: LogoResponse;
  kashFlowLogo: LogoResponse;
  oracleNetSuiteLogo: LogoResponse;
  prestaShopLogo: LogoResponse;
}

interface EnableIntegrationsRequests {
  toggleOracleNetsuite: RefetchFunction<{ enabled: boolean }, Integration>;
  toggleClearbooks: RefetchFunction<{ enabled: boolean }, Integration>;
  togglePrestaShop: RefetchFunction<{ enabled: boolean }, Integration>;
  toggleKashflow: RefetchFunction<{ enabled: boolean }, Integration>;
  toggleBigCommerce: RefetchFunction<{ enabled: boolean }, Integration>;
  logos: Logos;
  sendEvent: LogAmplitudeEvent;
}

const useEnableNoSetUpIntegrations = (
  useToggleIntegrationMutation = toggleIntegrationMutation,
  useGetLogoQuery = getLogoQuery
): EnableIntegrationsRequests => {
  const [logos, setLogos] = useState({} as Logos);
  const { sendEvent } = useAnalytics();

  const [, toggleClearbooks] = useToggleIntegrationMutation(
    accountingIntegrationsKeys.clearBooks
  );

  const [, toggleKashflow] = useToggleIntegrationMutation(
    accountingIntegrationsKeys.kashFlow
  );

  const [, toggleOracleNetsuite] = useToggleIntegrationMutation(
    accountingIntegrationsKeys.oracleNetSuite
  );

  const [, toggleBigCommerce] = useToggleIntegrationMutation(
    commerceIntegrationsKeys.bigCommerce
  );

  const [, togglePrestaShop] = useToggleIntegrationMutation(
    commerceIntegrationsKeys.prestaShop
  );

  const [{ data: kashFlowLogo }] = useGetLogoQuery(
    accountingIntegrationsKeys.kashFlow
  );

  useEffect(() => {
    if (kashFlowLogo) {
      setLogos((previous) => ({ ...previous, kashFlowLogo }));
    }
  }, [kashFlowLogo]);

  const [{ data: clearBooksLogo }] = useGetLogoQuery(
    accountingIntegrationsKeys.clearBooks
  );

  useEffect(() => {
    if (clearBooksLogo) {
      setLogos((previous) => ({ ...previous, clearBooksLogo }));
    }
  }, [clearBooksLogo]);

  const [{ data: oracleNetSuiteLogo }] = useGetLogoQuery(
    accountingIntegrationsKeys.oracleNetSuite
  );

  useEffect(() => {
    if (oracleNetSuiteLogo) {
      setLogos((previous) => ({ ...previous, oracleNetSuiteLogo }));
    }
  }, [oracleNetSuiteLogo]);

  const [{ data: bigCommerceLogo }] = useGetLogoQuery(
    commerceIntegrationsKeys.bigCommerce
  );

  useEffect(() => {
    if (bigCommerceLogo) {
      setLogos((previous) => ({ ...previous, bigCommerceLogo }));
    }
  }, [bigCommerceLogo]);

  const [{ data: prestaShopLogo }] = useGetLogoQuery(
    commerceIntegrationsKeys.prestaShop
  );

  useEffect(() => {
    if (prestaShopLogo) {
      setLogos((previous) => ({ ...previous, prestaShopLogo }));
    }
  }, [prestaShopLogo]);

  return {
    toggleClearbooks,
    toggleKashflow,
    toggleBigCommerce,
    toggleOracleNetsuite,
    togglePrestaShop,
    logos,
    sendEvent,
  };
};

export {
  useEnableNoSetUpIntegrations,
  EnableIntegrationsRequests,
  NoSetupIntegrations,
};
